import React, { useState, useEffect, useMemo, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "./App.css";
import HomePage from "./HomePage";
import ProblemsList from "./ProblemsList";
import ProblemDetails from "./ProblemDetails";
import UserLogin from "./UserLogin";
import UserStats from "./UserStats";
import ContestTracker from "./ContestTracker";
import Training from "./Training";
import Ranked from "./Ranked";

function App() {
  const [problems, setProblems] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // User authentication state
  const [username, setUsername] = useState(() => {
    return localStorage.getItem("codeforcesUsername") || "";
  });
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [userSubmissionsLoading, setUserSubmissionsLoading] = useState(false);
  const [userSubmissionsError, setUserSubmissionsError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [problemsPerPage] = useState(24);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("codeforcesTheme");
    return savedTheme ? JSON.parse(savedTheme) : false;
  });

  // Filter states
  const [ratingRange, setRatingRange] = useState({ min: "", max: "" });
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagMode, setTagMode] = useState("include");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("rating");
  const [sortOrder, setSortOrder] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "solved", "unsolved"

  // Available options (will be populated from API)
  const [availableTags, setAvailableTags] = useState([]);

  // Fetch user submissions
  const fetchUserSubmissions = async (handle) => {
    if (!handle) return;

    try {
      setUserSubmissionsLoading(true);
      setUserSubmissionsError(null);

      const response = await fetch(`https://codeforces.com/api/user.status?handle=${handle}&count=10000`);
      const data = await response.json();

      if (data.status === "OK") {
        setUserSubmissions(data.result);
        localStorage.setItem("codeforcesUserSubmissions", JSON.stringify(data.result));
        localStorage.setItem("codeforcesUserSubmissionsTimestamp", Date.now().toString());
      } else {
        throw new Error(data.comment || "Failed to fetch user submissions");
      }
    } catch (err) {
      setUserSubmissionsError(err.message);
      setUserSubmissions([]);
    } finally {
      setUserSubmissionsLoading(false);
    }
  };

  // Load cached user submissions
  const loadCachedUserSubmissions = useCallback(() => {
    const cachedSubmissions = localStorage.getItem("codeforcesUserSubmissions");
    const cachedTimestamp = localStorage.getItem("codeforcesUserSubmissionsTimestamp");
    const cachedUsername = localStorage.getItem("codeforcesUsername");

    if (cachedSubmissions && cachedTimestamp && cachedUsername === username) {
      const timestamp = parseInt(cachedTimestamp);
      const oneHour = 60 * 60 * 1000;

      if (Date.now() - timestamp < oneHour) {
        setUserSubmissions(JSON.parse(cachedSubmissions));
        return true;
      }
    }
    return false;
  }, [username]);

  // Handle username change
  const handleUsernameChange = (newUsername) => {
    setUsername(newUsername);
    localStorage.setItem("codeforcesUsername", newUsername);

    if (newUsername) {
      if (!loadCachedUserSubmissions()) {
        fetchUserSubmissions(newUsername);
      }
    } else {
      setUserSubmissions([]);
      localStorage.removeItem("codeforcesUserSubmissions");
      localStorage.removeItem("codeforcesUserSubmissionsTimestamp");
    }
  };

  // Load user submissions when username changes
  useEffect(() => {
    if (username) {
      if (!loadCachedUserSubmissions()) {
        fetchUserSubmissions(username);
      }
    }
  }, [username, loadCachedUserSubmissions]);

  const fetchProblems = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("https://codeforces.com/api/problemset.problems");
      const data = await response.json();

      if (data.status === "OK") {
        const problemsData = data.result.problems;
        const statisticsData = data.result.problemStatistics;

        // Extract unique tags and ratings
        const tags = [...new Set(problemsData.flatMap((p) => p.tags))].sort();
        const ratings = [...new Set(problemsData.map((p) => p.rating).filter((r) => r))].sort((a, b) => a - b);

        setProblems(problemsData);
        setStatistics(statisticsData);
        setAvailableTags(tags);

        // Cache data
        localStorage.setItem("codeforcesProblems", JSON.stringify(problemsData));
        localStorage.setItem("codeforcesStatistics", JSON.stringify(statisticsData));
        localStorage.setItem("codeforcesDataTimestamp", Date.now().toString());
        localStorage.setItem("codeforcesTags", JSON.stringify(tags));
        localStorage.setItem("codeforcesRatings", JSON.stringify(ratings));
      } else {
        throw new Error("Failed to fetch problems");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load cached data on mount
  useEffect(() => {
    const loadCachedData = () => {
      const cachedProblems = localStorage.getItem("codeforcesProblems");
      const cachedStatistics = localStorage.getItem("codeforcesStatistics");
      const cachedTimestamp = localStorage.getItem("codeforcesDataTimestamp");
      const cachedTags = localStorage.getItem("codeforcesTags");
      const cachedRatings = localStorage.getItem("codeforcesRatings");

      if (cachedProblems && cachedStatistics && cachedTimestamp && cachedTags && cachedRatings) {
        const timestamp = parseInt(cachedTimestamp);
        const oneHour = 60 * 60 * 1000;

        if (Date.now() - timestamp < oneHour) {
          setProblems(JSON.parse(cachedProblems));
          setStatistics(JSON.parse(cachedStatistics));
          setAvailableTags(JSON.parse(cachedTags));
          setLoading(false);
          return true;
        }
      }
      return false;
    };

    if (!loadCachedData()) {
      fetchProblems();
    }
  }, []);

  // Combine problems with statistics and user status
  const problemsWithStats = useMemo(() => {
    const statsMap = new Map();
    statistics.forEach((stat) => {
      statsMap.set(`${stat.contestId}-${stat.index}`, stat);
    });

    // Create a map of solved problems by the user
    const solvedProblemsMap = new Map();
    userSubmissions.forEach((submission) => {
      if (submission.verdict === "OK") {
        const key = `${submission.problem.contestId}-${submission.problem.index}`;
        solvedProblemsMap.set(key, true);
      }
    });

    return problems.map((problem) => {
      const stat = statsMap.get(`${problem.contestId}-${problem.index}`);
      const problemKey = `${problem.contestId}-${problem.index}`;
      const isSolved = solvedProblemsMap.has(problemKey);

      return {
        ...problem,
        solvedCount: stat ? stat.solvedCount : 0,
        isSolved: isSolved,
      };
    });
  }, [problems, statistics, userSubmissions]);

  // Filter and sort problems
  const filteredProblems = useMemo(() => {
    return problemsWithStats
      .filter((problem) => {
        // Status filter
        if (statusFilter === "solved" && !problem.isSolved) return false;
        if (statusFilter === "unsolved" && problem.isSolved) return false;

        // Rating range filter
        if (ratingRange.min || ratingRange.max) {
          if (!problem.rating) return false;
          if (ratingRange.min && problem.rating < parseInt(ratingRange.min)) return false;
          if (ratingRange.max && problem.rating > parseInt(ratingRange.max)) return false;
        }

        // Tag filter with mode
        if (selectedTags.length > 0) {
          if (tagMode === "include") {
            // Must include all selected tags (can have more)
            if (!selectedTags.every((tag) => problem.tags.includes(tag))) return false;
          } else if (tagMode === "includeOnly") {
            // Must include only the selected tags (no more, no less)
            const hasAll = selectedTags.every((tag) => problem.tags.includes(tag));
            const hasNoExtra = problem.tags.length === selectedTags.length;
            if (!hasAll || !hasNoExtra) return false;
          } else if (tagMode === "doesNotInclude") {
            // Must NOT include any of the selected tags
            if (selectedTags.some((tag) => problem.tags.includes(tag))) return false;
          } else if (tagMode === "includeSome") {
            // Must include at least includeSomeCount of the selected tags
            const count = selectedTags.filter((tag) => problem.tags.includes(tag)).length;
            if (count < 1) return false;
          }
        }

        // Search query
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          return problem.name.toLowerCase().includes(query) || problem.index.toLowerCase().includes(query) || (problem.contestId && problem.contestId.toString().includes(query));
        }

        return true;
      })
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "rating":
            aValue = a.rating || 0;
            bValue = b.rating || 0;
            break;
          case "solvedCount":
            aValue = a.solvedCount || 0;
            bValue = b.solvedCount || 0;
            break;
          case "name":
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case "contestId":
            aValue = a.contestId || 0;
            bValue = b.contestId || 0;
            break;
          default:
            aValue = a.rating || 0;
            bValue = b.rating || 0;
        }

        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
  }, [problemsWithStats, statusFilter, ratingRange, selectedTags, tagMode, searchQuery, sortBy, sortOrder]);

  // Pagination
  const indexOfLastProblem = currentPage * problemsPerPage;
  const indexOfFirstProblem = indexOfLastProblem - problemsPerPage;
  const currentProblems = filteredProblems.slice(indexOfFirstProblem, indexOfLastProblem);
  const totalPages = Math.ceil(filteredProblems.length / problemsPerPage);

  // Update current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, ratingRange, selectedTags, tagMode, searchQuery, sortBy, sortOrder]);

  // Helper functions
  const getRatingColor = (rating) => {
    if (rating < 1200) return "bg-gray-500 text-white";
    if (rating < 1400) return "bg-green-500 text-white";
    if (rating < 1600) return "bg-cyan-500 text-white";
    if (rating < 1900) return "bg-blue-500 text-white";
    if (rating < 2100) return "bg-purple-500 text-white";
    if (rating < 2400) return "bg-yellow-500 text-white";
    if (rating < 3000) return "bg-red-500 text-white";
    return "bg-red-600 text-white";
  };

  const getDifficultyText = (rating) => {
    if (rating < 1200) return "Newbie";
    if (rating < 1400) return "Pupil";
    if (rating < 1600) return "Specialist";
    if (rating < 1900) return "Expert";
    if (rating < 2100) return "Candidate Master";
    if (rating < 2400) return "Master";
    if (rating < 3000) return "International Master";
    return "Grandmaster";
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRefreshData = () => {
    localStorage.removeItem("codeforcesProblems");
    localStorage.removeItem("codeforcesStatistics");
    localStorage.removeItem("codeforcesDataTimestamp");
    localStorage.removeItem("codeforcesTags");
    localStorage.removeItem("codeforcesRatings");
    setLoading(true);
    fetchProblems();
  };

  const handleTagClick = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const toggleDarkMode = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem("codeforcesTheme", JSON.stringify(newTheme));
  };

  // Show login page if no username is set
  if (!username) {
    return <UserLogin onUsernameSubmit={handleUsernameChange} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />;
  }

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-all duration-500 ${
          isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
        }`}
      >
        <div className="text-center">
          <div className={`animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-6 ${isDarkMode ? "border-blue-400" : "border-blue-600"}`}></div>
          <p className={`text-lg font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>Loading Codeforces problems...</p>
          <p className={`text-sm mt-2 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>This may take a few seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-all duration-500 ${
          isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" : "bg-gradient-to-br from-red-50 to-pink-100"
        }`}
      >
        <div className="text-center">
          <div className={`p-4 rounded-full mb-6 ${isDarkMode ? "bg-red-500/20" : "bg-red-100"}`}>
            <i className="fas fa-exclamation-triangle text-red-500 text-3xl"></i>
          </div>
          <p className={`text-lg font-medium mb-4 ${isDarkMode ? "text-slate-300" : "text-red-600"}`}>{error}</p>
          <button
            onClick={handleRefreshData}
            className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:scale-105 ${
              isDarkMode
                ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
            }`}
          >
            <i className="fas fa-sync-alt mr-2"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} username={username} onUsernameChange={handleUsernameChange} />} />
        <Route
          path="/problems"
          element={
            <ProblemsList
              problemsWithStats={problemsWithStats}
              filteredProblems={filteredProblems}
              currentProblems={currentProblems}
              currentPage={currentPage}
              totalPages={totalPages}
              indexOfFirstProblem={indexOfFirstProblem}
              indexOfLastProblem={indexOfLastProblem}
              ratingRange={ratingRange}
              setRatingRange={setRatingRange}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              tagMode={tagMode}
              setTagMode={setTagMode}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              handleTagClick={handleTagClick}
              handlePageChange={handlePageChange}
              handleRefreshData={handleRefreshData}
              toggleDarkMode={toggleDarkMode}
              getRatingColor={getRatingColor}
              getDifficultyText={getDifficultyText}
              isDarkMode={isDarkMode}
              availableTags={availableTags}
              username={username}
              onUsernameChange={handleUsernameChange}
              userSubmissionsLoading={userSubmissionsLoading}
              userSubmissionsError={userSubmissionsError}
            />
          }
        />
        <Route
          path="/problem/:contestId/:index"
          element={
            <ProblemDetails
              problemsWithStats={problemsWithStats}
              handleTagClick={handleTagClick}
              getRatingColor={getRatingColor}
              getDifficultyText={getDifficultyText}
              isDarkMode={isDarkMode}
              username={username}
            />
          }
        />
        <Route
          path="/stats"
          element={
            <UserStats
              username={username}
              userSubmissions={userSubmissions}
              userSubmissionsLoading={userSubmissionsLoading}
              userSubmissionsError={userSubmissionsError}
              problemsWithStats={problemsWithStats}
              onUsernameChange={handleUsernameChange}
              toggleDarkMode={toggleDarkMode}
              isDarkMode={isDarkMode}
              getRatingColor={getRatingColor}
              getDifficultyText={getDifficultyText}
            />
          }
        />
        <Route
          path="/contest-tracker"
          element={
            <ContestTracker
              username={username}
              userSubmissions={userSubmissions}
              userSubmissionsLoading={userSubmissionsLoading}
              userSubmissionsError={userSubmissionsError}
              problemsWithStats={problemsWithStats}
              onUsernameChange={handleUsernameChange}
              toggleDarkMode={toggleDarkMode}
              isDarkMode={isDarkMode}
            />
          }
        />
        <Route
          path="/training"
          element={
            <Training
              username={username}
              problemsWithStats={problemsWithStats}
              availableTags={availableTags}
              toggleDarkMode={toggleDarkMode}
              isDarkMode={isDarkMode}
            />
          }
        />
        <Route
          path="/ranked"
          element={
            <Ranked
              username={username}
              problemsWithStats={problemsWithStats}
              toggleDarkMode={toggleDarkMode}
              isDarkMode={isDarkMode}
            />
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
